import { SwipeCard } from './SwipeCard';
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { loadSession } from '../../lib/session';
import { Icon } from '../ui/Icon';
import styles from './MailSurface.module.css';

export function GmailMark() {
  return <svg viewBox="0 0 48 36" width="20" height="15" role="img" aria-label="Gmail">
    <path fill="#4285F4" d="M3 36h7V15L0 7.5V33a3 3 0 0 0 3 3Z" />
    <path fill="#34A853" d="M38 36h7a3 3 0 0 0 3-3V7.5L38 15Z" />
    <path fill="#EA4335" d="M10 15V2.5L24 13 38 2.5V15L24 25.5Z" />
    <path fill="#C5221F" d="M0 7.5V5a5 5 0 0 1 8-4l2 1.5V15Z" />
    <path fill="#FBBC04" d="m38 2.5 2-1.5a5 5 0 0 1 8 4v2.5L38 15Z" />
  </svg>;
}

export function MailGlyph({ name, size = 15 }: { name: 'external' | 'reply' | 'clock'; size?: number }) {
  const paths = {
    external: 'M14 3h7v7M21 3 10 14M10 3H3v18h18v-7',
    reply: 'm9 4-6 6 6 6M3 10h10a7 7 0 0 1 7 7v3',
    clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 7v5l3 2',
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={paths[name]} /></svg>;
}

export function MailHint({ children, text }: { children: ReactNode; text: string }) {
  const id = useId();
  return <span className={styles.hint} tabIndex={0} aria-describedby={id}>
    {children}<span id={id} role="tooltip" className={styles.tooltip}>{text}</span>
  </span>;
}

export function MailSurface({ children, label, account, gmailUrl, demo = false }: {
  children: ReactNode; label: string; account?: string | undefined; gmailUrl?: string; demo?: boolean;
}) {
  return <section className={styles.surface} aria-label={label}>
    <div className={styles.chrome}>
      <span className={styles.identity}><GmailMark />
        {demo ? <a href="#" onClick={e => e.preventDefault()}>Open in Gmail <MailGlyph name="external" size={13} /></a>
          : gmailUrl ? <a href={gmailUrl} target="_blank" rel="noopener noreferrer">Open in Gmail <MailGlyph name="external" size={13} /></a> : <strong>{label}</strong>}
      </span>
      {account && <span className={styles.account} title={account}>{account}</span>}
      {demo && <span className={styles.example}>Example · not sent</span>}
    </div>
    <div className={styles.content}>{children}</div>
  </section>;
}

export function MailSubject({ subject, inbox = false }: { subject: string; inbox?: boolean }) {
  return <div className={styles.subject}><h3>{subject}</h3>{inbox && <span>Inbox</span>}</div>;
}

export function MailMessage({ from, to, subject, body, timestamp, children }: {
  from: string; to?: string; subject?: string | undefined; body?: string | undefined; timestamp?: string; children?: ReactNode;
}) {
  const sender = from.match(/^(.*?)\s*<([^>]+)>$/);
  return <div className={styles.message}>
    <span className={styles.avatar} aria-hidden="true">{(sender?.[1] || from).charAt(0).toUpperCase()}</span>
    <div className={styles.messageContent}>
      <div className={styles.senderLine}>
        <span><strong>{sender?.[1] || from}</strong>{sender && <span className={styles.address}> &lt;{sender[2]}&gt;</span>}</span>
        {timestamp && <span className={styles.timestamp}>{timestamp}</span>}
      </div>
      <details className={styles.envelope}>
        <summary>to {to || 'me'} <Icon name="chevron-down" size={12} /></summary>
        <dl><dt>from:</dt><dd>{from}</dd><dt>to:</dt><dd>{to || 'me'}</dd>{subject && <><dt>subject:</dt><dd>{subject}</dd></>}</dl>
      </details>
      {body !== undefined && <div className={styles.body}>{body || '\u00a0'}</div>}
      {children}
    </div>
  </div>;
}

export function MailComposer({ to, body, onChange, edited = false, ai = true, disabled = false, writing = false, showEditHint = true, children, fields, actions }: {
  to: string; body: string; onChange: (value: string) => void; edited?: boolean; ai?: boolean;
  disabled?: boolean; writing?: boolean; showEditHint?: boolean; children?: ReactNode; fields?: ReactNode; actions?: ReactNode;
}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const element = textarea.current;
    if (!element) return;
    const fit = () => {
      if (!element.clientWidth) return;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    };
    fit();
    // Refit wrapped text when the card resizes or its dropdown opens.
    if (typeof ResizeObserver === 'undefined' || !element.parentElement) return;
    let width = element.parentElement.clientWidth;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry || entry.contentRect.width === width) return;
      width = entry.contentRect.width;
      fit();
    });
    observer.observe(element.parentElement);
    return () => observer.disconnect();
  }, [body]);
  return <section className={styles.composer} aria-label="Reply composer">
    <div className={styles.composerHeader}>
      <span className={styles.replyTo}><MailGlyph name="reply" size={17} /><span>{to}</span></span>
      <span className={styles.aiBadge} data-ai={ai}>
        {ai && <Icon name="sparkles" size={13} />}
        {edited ? (ai ? 'AI draft · edited by you' : 'Edited by you') : ai ? (writing ? 'AI is writing…' : 'AI draft') : 'Template draft'}
      </span>
    </div>
    {fields}
    <div className={styles.writingArea}>
      <textarea ref={textarea} className={styles.replyBody} aria-label="Message" value={body}
        onChange={e => onChange(e.target.value)} readOnly={disabled} data-ai={ai && !edited}
        rows={1} spellCheck placeholder="Write your reply…" />
      {showEditHint && <span className={styles.editHint}>{disabled ? 'Reply locked' : writing ? 'Click here to take over' : 'Click anywhere to edit'}</span>}
    </div>
    {children}
    {actions && <div className={styles.composerActions}>{actions}</div>}
  </section>;
}

const gmailExample = {
  subject: 'Gmail in your chat', from: 'Echo <echo@example.com>', to: 'Echo (echo@example.com)',
  incoming: 'Easy to dismiss, send or collapse cards at the ready.',
};
export type EmailDemoConnection = {
  onConnect: () => void; disabled: boolean; pending: boolean; label: string; error?: string | undefined;
};
export function EmailDemo({ connectAction, connection }: { connectAction?: ReactNode; connection?: EmailDemoConnection | undefined }) {
  const [round, setRound] = useState(0);
  const [finished, setFinished] = useState(false);
  const [outcome, setOutcome] = useState<'sent' | 'dismissed'>('sent');
  const name = loadSession()?.bootstrap.user?.firstName?.trim() || 'there';
  const example = { ...gmailExample, reply: `Hi ${name},\n\nEcho only reads emails from your CRM contacts, with replies ready for your review.\n\nReuse messages with templates. Plan follow-ups with sequences.\n\nYou decide what gets sent.` };
  const restore = () => { setRound(value => value + 1); setFinished(false); };
  return <section className={styles.walkthrough} aria-label="Automatic reply example">
    {finished ? <div className={styles.demoComplete}>
      <strong>Example {outcome}</strong>
      <p>No email was sent. Connect Gmail to bring your conversations here.</p>
      <div className={styles.demoCompletionActions}>
        <button type="button" onClick={restore}>Undo</button>
        {connectAction}
      </div>
    </div> : <SwipeCard itemKey={String(round)}
      rightLabel={connection?.label ?? 'Send'} rightTone={connection ? 'blue' : 'green'} rightUndoable={!connection}
      previews={[{ title: 'Your conversations', caption: 'Always synced' }, { title: 'Your replies', caption: 'Ready to review' }]}
      onRight={connection ? (connection.disabled ? undefined : connection.onConnect) : () => { setOutcome('sent'); setFinished(true); }}
      onLeft={connection?.pending ? undefined : () => { setOutcome('dismissed'); setFinished(true); }}>
      {({ right, left, busy }) => <ExampleMessage key={round} example={example} onNext={right} onPrevious={left} busy={busy || !!connection?.pending} connection={connection} />}
    </SwipeCard>}
  </section>;
}
function ExampleMessage({ example, onNext, onPrevious, busy, connection }: {
  example: typeof gmailExample & { reply: string }; onNext: () => void; onPrevious: (() => void) | undefined; busy: boolean;
  connection?: EmailDemoConnection | undefined;
}) {
  const [body, setBody] = useState(example.reply);
  const [edited, setEdited] = useState(false);
  return <div className={styles.demoEmail} data-connect={!!connection}><MailSurface label="Example email" demo>
    <MailSubject subject={example.subject} />
    <MailMessage from={example.from} to="me" body={example.incoming} />
    <MailComposer to={example.to} body={body} edited={edited} writing={false} disabled={busy} showEditHint={false}
      onChange={value => { setEdited(true); setBody(value); }}
      actions={<><button type="button" className={styles.dismiss} disabled={!onPrevious || busy} onClick={onPrevious}>Dismiss</button>
        <button type="button" className={styles.primary} disabled={busy || connection?.disabled} onClick={onNext}>
          {connection ? <><GmailMark />{connection.label}</> : <>Send<span aria-hidden="true"> →</span></>}
        </button></>}>
      {connection?.error && <p role="alert" className={styles.connectionError}>{connection.error}</p>}
    </MailComposer>
  </MailSurface></div>;
}
